import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import DataTable, { DataTableMultiSelectFilter } from "../../../library/DataTable";
import IconButton, { StyledActionGroup, StyledIconLink } from "../../../library/IconButton";
import Modal from "../../../library/Modal";
import PermissionsGate from "../../../../utils/permissionGate";
import useDebounce from "../../../../hooks/useDebounce";
import { LinkedIntakeToProgramme, LinkedNetsuiteProductCourseList } from "../../../../services/ProgramService";
import { useLinkedProgrammeFilters, useLinkedProgrammeList } from "../../../../query/hooks/QueryHooksLinkedProgrammes";
import { appQueryClient } from "../../../../query/appQueryClient";
import { QUERY_KEYS } from "../../../../query/queryConsts";
import { RenderProgrammeType } from "../../../../utils/CommonGroupingItem";
import { exportTableData } from "../../../../utils/commonFunction";
import { getCampusText, getMethodText, sortByLabel } from "../../../../utils/LinkedProgrammeUtils";
import { getRowsPerPageOptions, STANDARD_ROWS_PER_PAGE_SIZES } from "../studyPaths/studyPaths.utils";
import { ProgrammeDetailsCell } from "./LinkedProgrammeRowCells";

const EXPORT_HEADERS = ["Name", "Campus", "Delivery Method", "Intake", "Programme Type", "Students"];

/**
 * Link Programmes modal: search/filter the course intakes on this PMC's Netsuite product that
 * aren't linked yet, and link one - same Modal/DataTable shell as the Assign Modules modal.
 * @param {number|string} pmcId The PMC programme id.
 * @param {string|number} netsuiteProductId The PMC's Netsuite product id.
 * @param {*} refreshSignal Bumped by the parent after any list change, so this list refetches too.
 * @param {Function} onLinked Called after a programme is linked, to refresh the parent list.
 * @param {Function} onClose Closes the modal.
 */
function LinkProgrammesModal({ pmcId, netsuiteProductId, refreshSignal, onLinked, onClose }) {
    const [search, setSearch] = useState("");
    const [intake, setIntake] = useState([]);
    const [campus, setCampus] = useState([]);
    const [delivery, setDelivery] = useState([]);
    const [programmeType, setProgrammeType] = useState([]);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(STANDARD_ROWS_PER_PAGE_SIZES[0]);
    const [sortKey, setSortKey] = useState("name");
    const [sortOrder, setSortOrder] = useState("ASC");
    const [linkingId, setLinkingId] = useState(null);
    const debouncedSearch = useDebounce(search, 500);

    const requestData = useMemo(() => ({
        programme_id: pmcId,
        netsuite_product_id: netsuiteProductId,
        isAssigned: 0,
        page,
        limit: rowsPerPage,
        key: sortKey,
        sort: sortOrder,
        search: debouncedSearch,
        intake,
        viaCampus: campus,
        viaLearnType: delivery,
        viaCourseType: programmeType,
    }), [pmcId, netsuiteProductId, page, rowsPerPage, sortKey, sortOrder, debouncedSearch, intake, campus, delivery, programmeType]);

    const { data: dropdown } = useLinkedProgrammeFilters(pmcId, netsuiteProductId, 0);
    const { data, isLoading } = useLinkedProgrammeList(requestData, refreshSignal, Boolean(netsuiteProductId));
    const totalPages = Math.max(1, Math.ceil(data.totalRows / rowsPerPage));

    const handleReset = () => {
        setSearch("");
        setIntake([]);
        setCampus([]);
        setDelivery([]);
        setProgrammeType([]);
        setPage(1);
    };

    const handleLink = async (programme) => {
        setLinkingId(programme.id);

        try {
            await LinkedIntakeToProgramme({
                programme_id: programme.id,
                netsuite_id: netsuiteProductId,
                PMC_programme_id: pmcId,
            });
            await appQueryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.linkedProgrammes.root, QUERY_KEYS.linkedProgrammes.filters],
            });
            Swal.fire({ icon: "success", title: "Programme Linked Successfully" });
            onLinked();
        } catch (error) {
            console.log("LinkProgrammesModal.js->handleLink->Error", error);
            Swal.fire("Error", error?.response?.data?.message || "Unable to link programme. Please try again.", "error");
        } finally {
            setLinkingId(null);
        }
    };

    const handleExport = async (fileType) => {
        try {
            const response = await LinkedNetsuiteProductCourseList({ ...requestData, exportStatus: "true" });
            const rows = (response.data?.data ?? []).map((programme) => ({
                Name: programme.name || "-",
                Campus: getCampusText(programme.campus),
                "Delivery Method": getMethodText(programme.type),
                Intake: programme.intakes || "-",
                "Programme Type": programme.subject_type ? RenderProgrammeType(programme.subject_type).text : "-",
                Students: programme.total_student ?? "-",
            }));
            exportTableData(fileType, "Link Programmes", EXPORT_HEADERS, rows);
        } catch (error) {
            console.log("LinkProgrammesModal.js->handleExport->Error", error);
            Swal.fire("Error", error?.response?.data?.message || "Unable to export programmes. Please try again.", "error");
        }
    };

    const filters = [
        { key: "intake", placeholder: "Intake", value: intake, setValue: setIntake, options: sortByLabel(dropdown.intake, true) },
        { key: "campus", placeholder: "Campus", value: campus, setValue: setCampus, options: sortByLabel(dropdown.campus) },
        { key: "method", placeholder: "Delivery Method", value: delivery, setValue: setDelivery, options: sortByLabel(dropdown.deliveryMethod) },
        { key: "programmeType", placeholder: "Programme Type", value: programmeType, setValue: setProgrammeType, options: sortByLabel(dropdown.programmeType) },
    ];

    const columns = [
        {
            key: "name",
            label: "Programme Details",
            render: (programme) => <ProgrammeDetailsCell row={programme} />,
        },
        {
            key: "intakes",
            label: "Intake",
            render: (programme) => programme.intakes || "-",
        },
        {
            key: "subject_type",
            label: "Programme Type",
            render: (programme) => (programme.subject_type ? RenderProgrammeType(programme.subject_type).text : "-"),
        },
        {
            key: "total_student",
            label: "",
            icon: "users",
            ariaLabel: "Students",
            align: "center",
            render: (programme) => programme.total_student ?? "-",
        },
        {
            key: "action",
            label: "Action",
            sortable: false,
            width: "1%",
            render: (programme) => (
                <StyledActionGroup>
                    <PermissionsGate
                        scopes={["matpedit"]}
                        RenderError={() => <IconButton icon="folder-open" variant="solid" disabled ariaLabel="Open programme" />}
                    >
                        <StyledIconLink
                            to={`/courseAdministration/Programmes/programme/open/${programme.id}/details`}
                            title="Open programme"
                        >
                            <IconButton icon="folder-open" variant="solid" ariaLabel="Open programme" />
                        </StyledIconLink>
                    </PermissionsGate>

                    <PermissionsGate
                        scopes={["matpadd"]}
                        RenderError={() => <IconButton icon="link" variant="solid" disabled ariaLabel="Link programme" />}
                    >
                        <IconButton
                            icon="link"
                            variant="solid"
                            ariaLabel={`Link ${programme.name}`}
                            title={`Link ${programme.name}`}
                            disabled={linkingId !== null}
                            disabledReason="A programme is currently being linked"
                            onClick={() => handleLink(programme)}
                        />
                    </PermissionsGate>
                </StyledActionGroup>
            ),
        },
    ];

    return (
        <Modal title="Link Programmes" onClose={onClose} width={1100} headerTone="brand">
            <DataTable
                columns={columns}
                data={data.listData}
                keyField="id"
                loading={isLoading}
                emptyMessage="No programmes match your search and filters."
                sortKey={sortKey}
                sortOrder={sortOrder}
                onSortChange={(field, nextOrder) => {
                    setSortKey(field);
                    setSortOrder(nextOrder);
                }}
                searchValue={search}
                onSearchChange={(value) => { setSearch(value); setPage(1); }}
                onReset={handleReset}
                exportOptions={[
                    { key: "excel", icon: "file-excel", ariaLabel: "Export as Excel", onClick: () => handleExport("xlsx") },
                    { key: "csv", icon: "file-csv", ariaLabel: "Export as CSV", onClick: () => handleExport("csv") },
                    { key: "pdf", icon: "file-pdf", ariaLabel: "Export as PDF", onClick: () => handleExport("pdf") },
                ]}
                filterSlot={filters.map((filter) => (
                    <DataTableMultiSelectFilter
                        key={filter.key}
                        placeholder={filter.placeholder}
                        value={filter.value}
                        onChange={(event) => { filter.setValue(event.target.value); setPage(1); }}
                        options={filter.options}
                    />
                ))}
                pagination={{
                    totalEntries: data.totalRows,
                    rangeStart: (page - 1) * rowsPerPage + 1,
                    rangeEnd: Math.min(page * rowsPerPage, data.totalRows),
                    rowsPerPage,
                    rowsPerPageOptions: getRowsPerPageOptions(data.totalRows),
                    onRowsPerPageChange: (value) => { setRowsPerPage(value); setPage(1); },
                    currentPage: page,
                    totalPages,
                    onPageChange: setPage,
                }}
            />
        </Modal>
    );
}

LinkProgrammesModal.displayName = "LinkProgrammesModal";

export default LinkProgrammesModal;
